import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    archiveScorecardRow,
    ArchiveScorecardRowResponse,
    createScorecardFromTemplate,
    createScorecardRow,
    CreateFromTemplateRequest,
    CreateScorecardRowRequest,
    CreateScorecardRowResponse,
    CreateTemplateFromSiteRequest,
    createTemplateFromSite,
    deleteScorecardDataCell,
    deleteScorecardDocument,
    deleteScorecardRowData,
    deleteScorecardTemplate,
    downloadScorecardTemplate,
    exportKPISummary,
    getKPISummary,
    getScorecardConfig,
    getScorecardData,
    getScorecardDocuments,
    getScorecardTemplates,
    getSiteFurtherInfo,
    KPISummaryExportRequest,
    KPISummaryItem,
    KPISummaryRequest,
    reviewScorecardDataCell,
    ReviewScorecardDataResponse,
    ScorecardConfigResponse,
    ScorecardDataItem,
    ScorecardDataParams,
    ScorecardDocument,
    ScorecardNotes,
    ScorecardTemplate,
    updateScorecardNotes,
    updateScorecardRow,
    UpdateScorecardRowRequest,
    UpdateScorecardRowResponse,
    updateSiteFurtherInfo,
    UpdateSiteFurtherInfoRequest,
    UpdateSiteFurtherInfoResponse,
    uploadScorecardDocuments,
    uploadScorecardTemplate,
    UploadTemplateRequest,
    unreviewScorecardDataCell
} from "api";
import { QUERY_KEYS } from "consts";
import { useNotify } from "hooks";

export const useScorecardDataBySite = (params: ScorecardDataParams, enabled: boolean = true) =>
    useQuery<ScorecardDataItem[]>(
        [QUERY_KEYS.SCORECARD_DATA, params],
        () => getScorecardData(params),
        { enabled }
    );

export const useDeleteScorecardRow = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    return useMutation<
        void,
        AxiosError,
        number
    >(
        (rowId: number) => deleteScorecardRowData(rowId),
        {
            onSuccess: () => {
                notify.success('Scorecard row successfully deleted.');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error?.response?.data === 'string' ? error.response.data : 'Failed to delete scorecard row');
            },
        }
    );
};

export const useScorecardConfig = () =>
    useQuery<ScorecardConfigResponse>([QUERY_KEYS.SCORECARD_CONFIG], getScorecardConfig);

export const useCreateScorecardRow = () => {
    const queryClient = useQueryClient();

    return useMutation<CreateScorecardRowResponse, Error, CreateScorecardRowRequest>(
        createScorecardRow,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            }
        }
    );
};

export const useUpdateScorecardRow = () => {
    const queryClient = useQueryClient();

    return useMutation<UpdateScorecardRowResponse, Error, { id: number; data: UpdateScorecardRowRequest }>(
        ({ id, data }) => updateScorecardRow(id, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            }
        }
    );
};

export const useArchiveScorecardRow = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    return useMutation<
        ArchiveScorecardRowResponse,
        AxiosError,
        { id: number; archived: boolean }
    >(
        ({ id, archived }) => archiveScorecardRow(id, { archived }),
        {
            onSuccess: (data) => {
                const action = data.archived ? 'archived' : 'restored';
                notify.success(`Scorecard row successfully ${action}.`);
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error?.response?.data === 'string' ? error.response.data : 'Failed to archive scorecard row');
            },
        }
    );
};

export const useScorecardTemplates = () =>
    useQuery<ScorecardTemplate[]>([QUERY_KEYS.SCORECARD_TEMPLATES], getScorecardTemplates);

export const useCreateTemplateFromSite = () => {
    const queryClient = useQueryClient();

    return useMutation<ScorecardTemplate, Error, { siteId: number; data: CreateTemplateFromSiteRequest }>(
        ({ siteId, data }) => createTemplateFromSite(siteId, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_TEMPLATES]);
            }
        }
    );
};

export const useDeleteScorecardTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>(
        deleteScorecardTemplate,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_TEMPLATES]);
            }
        }
    );
};

export const useCreateScorecardFromTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, CreateFromTemplateRequest>(
        createScorecardFromTemplate,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            }
        }
    );
};

export const useDownloadScorecardTemplate = () =>
    useMutation<Blob, Error, number>(downloadScorecardTemplate);

export const useUploadScorecardTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, UploadTemplateRequest>(
        uploadScorecardTemplate,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
        }
    );
};

export const useKPISummary = (params: KPISummaryRequest, enabled: boolean = true) =>
    useQuery<KPISummaryItem[]>(
        [QUERY_KEYS.KPI_SUMMARY, params],
        () => getKPISummary(params),
        { enabled }
    );

export const useExportKPISummary = () => {
    const notify = useNotify();

    return useMutation<Blob, AxiosError, KPISummaryExportRequest>(
        exportKPISummary,
        {
            onSuccess: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'kpi_summary_export.csv';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                notify.success('KPI Summary exported successfully.');
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error?.response?.data === 'string' ? error.response.data : 'Failed to export KPI Summary');
            },
        }
    );
};

export const useUpdateScorecardNotes = () => {
    const queryClient = useQueryClient();

    return useMutation<ScorecardNotes, Error, { dataId: number; notes: string }>(
        ({ dataId, notes }) => updateScorecardNotes(dataId, notes),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
        }
    );
};

export const useScorecardDocuments = (dataId: number, enabled: boolean = true) =>
    useQuery<ScorecardDocument[]>(
        [QUERY_KEYS.SCORECARD_DOCUMENTS, dataId],
        () => getScorecardDocuments(dataId),
        { enabled: dataId > 0 && enabled }
    );

export const useUploadScorecardDocuments = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation<ScorecardDocument[], AxiosError, { dataId: number; files: File[] }>(
        ({ dataId, files }) => uploadScorecardDocuments(dataId, files),
        {
            onSuccess: () => {
                notify.success('Documents uploaded successfully!');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DOCUMENTS]);
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
        }
    );
};

export const useDeleteScorecardDocument = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation<void, AxiosError, number>(
        (documentId: number) => deleteScorecardDocument(documentId),
        {
            onSuccess: () => {
                notify.success('Document deleted successfully!');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DOCUMENTS]);
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error?.response?.data === 'string' ? error.response.data : 'Failed to delete document');
            },
        }
    );
};

export const useSiteFurtherInfo = (siteId: number, enabled: boolean = true) =>
    useQuery<string>(
        [QUERY_KEYS.SCORECARD_DATA, 'further-info', siteId],
        () => getSiteFurtherInfo(siteId),
        { enabled }
    );

export const useUpdateSiteFurtherInfo = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    return useMutation<
        UpdateSiteFurtherInfoResponse,
        AxiosError,
        UpdateSiteFurtherInfoRequest
    >(
        updateSiteFurtherInfo,
        {
            onSuccess: () => {
                notify.success('Further information updated successfully.');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA, 'further-info']);
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error?.response?.data === 'string' ? error.response.data : 'Failed to update further information');
            },
        }
    );
};

export const useReviewScorecardDataCell = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    return useMutation<ReviewScorecardDataResponse, AxiosError, number>(
        (scorecardDataId: number) => reviewScorecardDataCell(scorecardDataId),
        {
            onSuccess: () => {
                notify.success('Cell value marked as reviewed successfully');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
        }
    );
};

export const useUnreviewScorecardDataCell = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    return useMutation<ReviewScorecardDataResponse, AxiosError, number>(
        (scorecardDataId: number) => unreviewScorecardDataCell(scorecardDataId),
        {
            onSuccess: () => {
                notify.success('Cell value marked as unreviewed successfully');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
        }
    );
};

export const useDeleteScorecardDataCell = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    return useMutation<void, AxiosError, number>(
        (scorecardDataId: number) => deleteScorecardDataCell(scorecardDataId),
        {
            onSuccess: () => {
                notify.success('Cell data deleted successfully');
                queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error?.response?.data === 'string' ? error.response.data : 'Failed to delete cell data');
            },
        }
    );
};